<template>
  <div class="app-wrapper">
    <div v-if="!isProductionSite" class="non-production-banner">
      ⚠️ THIS IS A NON-PRODUCTION SITE USED FOR STAGING/DEVELOPMENT PURPOSES ONLY ⚠️
    </div>
    <router-view v-if="!appDataStore.isLoading || router.currentRoute.value.name === RouteName.LOGIN" />
    <div v-if="appDataStore.isLoading && userDataStore.getAuthToken !== null" style="width: 100%; height: 100vh">
      <LoadingCarWheelSpinner />
    </div>
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
      :timeout="notification.timeout"
    >
      <div class="secondary-font" style="font-size: 14px; font-weight: 600">
        {{ notification.message }}
      </div>
      <div
        v-if="notification.caption"
        class="secondary-font"
        style="font-size: 14px; font-weight: 600"
      >
        {{ notification.caption }}
      </div>
      <template v-slot:actions>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="notification.show = false"
        />
      </template>
    </v-snackbar>
  </div>
  <Footer v-if="!appDataStore.isLoading || router.currentRoute.value.name === RouteName.LOGIN" />
  
  <!-- Floating Unpaid Balance - Site-wide -->
  <FloatingUnpaidBalance v-if="!appDataStore.isLoading" />
</template>

<script lang="ts">
// Components
import FloatingUnpaidBalance from '@/components/floating/FloatingUnpaidBalance.vue';
import Footer from '@/components/shared/Footer.vue';
import LoadingCarWheelSpinner from '@/components/shared/LoadingCarWheelSpinner.vue';

// Stores
import { useUserDataStore } from '@/stores/userDataStore';
import { useAppDataStore } from '@/stores/appDataStore';

// Types
import { NotificationService } from '@/utils/notificationService';
import { RouteName } from '@/router/routes';

// Utils
import { AccessDeniedError } from '@/api/apiCalls';
import { createNotificationService } from '@/utils/notificationService';
import { fetchInitData } from '@/utils/initData';

// Vue
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
} from 'vue';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'App',
  components: {
    FloatingUnpaidBalance,
    Footer,
    LoadingCarWheelSpinner,
  },
  setup() {
    // State

    const notification = reactive<NotificationService>(
      createNotificationService()
    );
    const appDataStore = reactive(useAppDataStore());
    const userDataStore = reactive(useUserDataStore());
    const router = useRouter();

    // Lifecycle

    onMounted(async (): Promise<void> => {
      if (!appDataStore.getIsInitialLoadComplete && userDataStore.getAuthToken !== null) {
        appDataStore.setIsLoading(true);
        try {
          await fetchInitData();
        } catch (err) {
          if (err instanceof AccessDeniedError) {
            userDataStore.logout();
            router.push(RouteName.LOGIN);
            notification.notify({
              message: 'Registration is not currently open',
              caption: 'Please check back closer to the registration opening date.',
              color: 'red',
              timeout: 8000,
            });
          } else {
            notification.error({ error: err });
          }
        } finally {
          appDataStore.setIsLoading(false);
        }
      }

      console.log('VUE_APP_DEPLOYMENT_ENV: ', process.env.VUE_APP_DEPLOYMENT_ENV);
      console.log('isProductionSite: ', isProductionSite.value);
    });

    // Computed

    const isProductionSite = computed(() => {
      return process.env.VUE_APP_DEPLOYMENT_ENV === 'production';
    });

    // Methods

    return { appDataStore, isProductionSite, notification, router, RouteName, userDataStore };
  },
});
</script>

<style lang="scss">
.app-wrapper {
  background-color: $secondary;
  min-height: calc(100vh - 88px);
}

.form-btn-wrapper {
  max-width: 600px;
  width: 100%;
}

.form-btn {
  background-color: $pca-blue !important;
  color: white !important;
  font-family: 'Saira Condensed', sans-serif;
  font-weight: 600 !important;
  height: 32px
}

.required-asterisk {
  color: $pca-red;
  font-size: 16px;
  font-weight: 600;
}

.not-attending-parade-text {
  color: $pca-red;
  font-size: 12px;
  font-weight: 400;
  margin: 0px 0px 0px 8px;
}

.non-production-banner {
  background-color: red;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 5px;
  text-align: center;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 9000;
}

.centered-page-card {
  border-radius: 20px !important;
  margin-bottom: 32px !important;
  max-width: 600px;
  padding: 20px !important;
  width: 100%;
}

.font-14 {
  font-size: 14px;
}

.font-18 {
  font-size: 18px;
}

.v-tooltip {
  .v-overlay__content {
    background-color: $pca-light-blue !important;
    color: white !important;
    font-family: 'Montserrat', sans-serif !important;
    font-size: 12px !important;
    font-weight: 600;
    border-radius: 4px !important;
    padding: 4px 8px !important;
  }
}

.loading {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100vh;
  justify-content: center;
  width: 100vw;
}

.primary-font {
  font-family: 'Saira Condensed', sans-serif;
  font-optical-sizing: auto;
  font-weight: 200;
  font-style: normal;
}

.pca-blue-background {
  background-color: $pca-blue;
}

.secondary-font {
  font-family: 'Montserrat', sans-serif;
  font-optical-sizing: auto;
  font-weight: 200;
  font-style: normal;
}

.secondary-font-14-600 {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.secondary-font-16-600 {
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.secondary-font-18-600 {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  font-weight: 600;
}

.background-color-pca-blue {
  background-color: $pca-blue;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: 'Saira Condensed', sans-serif;
  font-weight: 300;
  font-style: normal;
  letter-spacing: 0px;
}

.form-card-title {
  color: $pca-blue;
  font-family: 'Saira Condensed', sans-serif;
  font-size: 25px;
  font-weight: 600;
}

.form-card-title-date {
  color: $pca-blue;
  margin-top: -10px;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.small-home-page-card-title {
  color: $pca-blue;
  font-family: 'Saira Condensed', sans-serif;
  font-size: 25px;
  font-weight: 500;
}

.not-secured-text {
  font-size: 14px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
}

.v-btn {
  letter-spacing: 0px !important;
  text-transform: none !important;
}

.underlined-text-button {
  background-color: transparent;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px !important;
  font-weight: 600 !important;
  height: 17px !important;
  margin: -3px 0px 0px 0px;
  text-decoration: underline !important;
  width: 80px !important;
}

.edit-badge-name-button {
  background-color: transparent;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px !important;
  font-weight: 600 !important;
  height: 17px !important;
  text-decoration: underline !important;
  width: 129px !important;
}

.v-btn-toggle {
  font-family: 'Saira Condensed', sans-serif;
  height: 32px !important;
  width: 128px;
}

.v-checkbox .v-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.pca-blue {
  color: $pca-blue;
}

.pca-light-blue {
  color: $pca-light-blue;
}

.pca-dark-blue {
  color: $pca-dark-blue;
}

.pca-red {
  color: $pca-red;
}

.pca-dark-red {
  color: $pca-dark-red;
}

.pca-light-grey {
  color: $pca-light-grey;
}

.pca-dark-grey {
  color: $pca-dark-grey;
}

.custom-input-error-text {
  color: #B00020;
  font-size: 12px;
  font-weight: 400;
  padding-left: 16px;
}

.dropdown-menu-content {
  .v-list-item {
    min-height: 32px !important;
    padding: 0 8px !important;
  }

  .v-list-item-title {
    font-size: 14px !important;
  }
}

.nested-list-indent {
  margin-left: 1.25rem;
}

strong {
  font-weight: 750 !important;
}

@media (max-width: 1180px) {
  .small-home-page-card {
    width: 100% !important;
    max-width: 460px !important;
  }
}

@media (max-width: 775px) {
  .app-wrapper {
    min-height: calc(100vh - 182px);
  }
}
</style>
